/**
 * Cálculo de promociones de producto (descuento % y promos por cantidad NxM).
 * Misma lógica que CartaOrderShop / backend LocalOrderStrategyImpl.
 */

export const isPromotionActive = (promotion) => {
  if (!promotion) return false;
  if (promotion.deletedAt) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (promotion.startDate) {
    const start = new Date(promotion.startDate);
    start.setHours(0, 0, 0, 0);
    if (start > today) return false;
  }
  if (promotion.endDate) {
    const end = new Date(promotion.endDate);
    end.setHours(23, 59, 59, 999);
    if (end < today) return false;
  }
  return true;
};

export const calculatePromotionForProduct = (product, quantity) => {
  if (!product || !quantity || quantity <= 0) {
    return null;
  }

  const promotion = product.activePromotion || product.promotion?.promotion;
  if (!isPromotionActive(promotion)) {
    return null;
  }

  if (product.bulkSale && promotion.promotionType === 'QUANTITY_PROMOTION') {
    return null;
  }

  const unitPrice = Number(product.price);
  if (Number.isNaN(unitPrice)) {
    return null;
  }

  const originalSubTotal = unitPrice * quantity;

  if (promotion.promotionType === 'QUANTITY_PROMOTION') {
    const buyQuantity = Number(promotion.buyQuantity);
    const payQuantity = Number(promotion.payQuantity);

    if (!buyQuantity || !payQuantity) {
      return null;
    }

    const groups = Math.floor(quantity / buyQuantity);
    const remainder = quantity % buyQuantity;
    const quantityPaid = groups > 0 ? (groups * payQuantity) + remainder : quantity;
    const finalSubTotal = Number((unitPrice * quantityPaid).toFixed(2));
    const discountAmount = Number((originalSubTotal - finalSubTotal).toFixed(2));

    return {
      promotion,
      promotionType: 'QUANTITY_PROMOTION',
      applied: groups > 0,
      quantitySold: quantity,
      quantityPaid,
      groups,
      remainder,
      originalSubTotal: Number(originalSubTotal.toFixed(2)),
      finalSubTotal,
      discountAmount: Math.max(discountAmount, 0),
    };
  }

  if (promotion.promotionType === 'PERCENTAGE_DISCOUNT') {
    const discountPercentage = Number(promotion.discountPercentage);
    if (!discountPercentage || discountPercentage <= 0) {
      return null;
    }
    const discountAmount = Number((originalSubTotal * (discountPercentage / 100)).toFixed(2));
    const finalSubTotal = Number((originalSubTotal - discountAmount).toFixed(2));

    return {
      promotion,
      promotionType: 'PERCENTAGE_DISCOUNT',
      applied: discountAmount > 0,
      quantitySold: quantity,
      quantityPaid: quantity,
      originalSubTotal: Number(originalSubTotal.toFixed(2)),
      finalSubTotal,
      discountAmount: Math.max(discountAmount, 0),
    };
  }

  return null;
};

const calculateBulkPercentagePromotion = (item) => {
  const promotion = item.activePromotion || item.promotion?.promotion;
  if (!isPromotionActive(promotion) || promotion.promotionType !== 'PERCENTAGE_DISCOUNT') {
    return null;
  }
  const originalSubTotal = Number(item.subTotal);
  if (!originalSubTotal || originalSubTotal <= 0) return null;
  const discountPercentage = Number(promotion.discountPercentage);
  if (!discountPercentage || discountPercentage <= 0) return null;
  const discountAmount = Number((originalSubTotal * (discountPercentage / 100)).toFixed(2));
  const finalSubTotal = Number((originalSubTotal - discountAmount).toFixed(2));
  return {
    promotion,
    promotionType: 'PERCENTAGE_DISCOUNT',
    applied: discountAmount > 0,
    quantitySold: 1,
    quantityPaid: 1,
    originalSubTotal,
    finalSubTotal,
    discountAmount: Math.max(discountAmount, 0),
  };
};

export const applyPromotionToCartItem = (item) => {
  if (!item) return item;
  if (item.bulkSale && item.subTotal != null) {
    const promotion = calculateBulkPercentagePromotion(item);
    return {
      ...item,
      price: Number(item.price),
      cant: 1,
      promotion,
    };
  }
  const quantity = item.cant ?? item.quantity ?? 0;
  const promotion = calculatePromotionForProduct(item, quantity);
  return {
    ...item,
    price: Number(item.price),
    cant: quantity,
    promotion,
  };
};

export const getCartLineOriginalSubtotal = (item) => {
  if (!item) return 0;
  if (item.bulkSale && item.subTotal != null) {
    return Number(item.subTotal);
  }
  return Number(item.price) * Number(item.cant);
};

export const getCartLineSubtotal = (item) => {
  if (!item) return 0;
  const promotion = item.promotion;
  if (promotion && isPromotionActive(promotion.promotion)) {
    return promotion.finalSubTotal ?? getCartLineOriginalSubtotal(item);
  }
  return getCartLineOriginalSubtotal(item);
};

export const getCartPromotionDiscount = (item) => {
  const promotion = item?.promotion;
  if (!promotion || !promotion.applied) return 0;
  return promotion.discountAmount ?? 0;
};

export const formatPromotionLabel = (promotion) => {
  if (!promotion) return '';
  if (promotion.promotionType === 'QUANTITY_PROMOTION') {
    return `${promotion.buyQuantity}x${promotion.payQuantity}`;
  }
  if (promotion.promotionType === 'PERCENTAGE_DISCOUNT') {
    return `-${promotion.discountPercentage}%`;
  }
  return 'PROMO';
};

export const calculateCartTotal = (items) => {
  if (!items?.length) return 0;
  return items.reduce((total, item) => total + getCartLineSubtotal(item), 0);
};
